import { $t } from '@/i18n';
import { XySourceCgiEditMeta, XySourceMethodEditMeta } from '../paramsFormComponents';
import { CATEGORY } from './const';
import { deepInterop } from '@tencent/ui-core/lib/utils/stringInterop';
import { splitPath } from '@tencent/data-schema-core/lib/utils/path';
import { get, set } from 'lodash';
import { getDataSourceMethodReturnSchema } from '@components/uicorePlugin/utils/getDataSourceReturnSchema';
// 调用数据源接口
export const cgiAtom = {
    id: 'xy:source:requestCgi',
    name: $t('k_bd07b32b'),
    desc: $t('k_ec59b262'),
    category: CATEGORY.DATASOURCE,
    visible: false,
    paramsSchema: {
        type: 'object',
        fields: [
            {
                id: 'cgiInfo',
                type: 'any',
                title: '',
                default: { sourceId: '', cgi: '', code: '', paramType: 'js' },
                ui: { type: 'XySourceCgiEdit' },
            },
        ],
    },
    paramsFormComponents: [XySourceCgiEditMeta],
    hideReturnValueSchema: true,
    async execute(ctx, params) {
        const { sourceId, cgi, code, paramType, paramObject } = params.cgiInfo || {};
        const state = ctx.renderer.sources[sourceId]?.data;
        const request = state?.$requests[cgi];
        if (typeof request !== 'function') {
            throw new Error($t('k_409f979f', { arg_0: cgi }));
        }
        const fetchFn = async (req) => {
            try {
                const res = await request(req);
                return res;
            }
            catch (error) {
                return {
                    error,
                };
            }
        };
        if (!paramType || paramType === 'js') {
            const httpParams = ctx.tryEval(code); // 空也能调用
            if (Array.isArray(httpParams)) {
                // 批量 请求
                const promises = httpParams.map(p => fetchFn(p));
                return await Promise.all(promises);
            }
            return await fetchFn(httpParams);
        }
        if (paramType === 'form') {
            const { body, query, bodyExprStr } = paramObject;
            if (state.promiseForCollection) {
                // 同步加载的数据源，内部collection对象是异步加载的，需要等待
                await state.promiseForCollection;
            }
            const isDB = state.collection.collectionData.type === 'db';
            const queryValue = deepInterop(query, ctx.interopContext, ctx.extraInteropContext);
            let bodyValue = deepInterop(isDB ? bodyExprStr : body, ctx.interopContext, ctx.extraInteropContext);
            if (isDB && typeof bodyValue === 'string') {
                try {
                    bodyValue = JSON.parse(bodyValue);
                }
                catch (error) {
                    console.error($t('k_1fff1378'), error);
                }
            }
            const params = {
                query: queryValue,
                body: bodyValue,
            };
            return await fetchFn(params);
        }
    },
    returnValue: {
        type: 'object',
        fields: [
            {
                id: 'body',
                type: 'object',
                description: $t('k_d51661c7'),
            },
            {
                id: 'error',
                type: 'object',
                description: $t('k_c9e1a874'),
            },
        ],
    },
};
let showBatchCgiAtom = false;
try {
    const lsShowBatch = localStorage.getItem('xy_showBatchCgiAtom');
    showBatchCgiAtom = Boolean(lsShowBatch);
}
catch (error) {
}
// 批量调用数据源接口
export const batchCgiAtom = {
    id: 'xy:source:batchRequestCgi',
    name: $t('k_5e33e7e7'),
    desc: $t('k_8d8070e0'),
    category: CATEGORY.DATASOURCE,
    visible: showBatchCgiAtom,
    paramsSchema: {
        type: 'object',
        fields: [
            {
                id: 'cgiInfo',
                type: 'any',
                title: '',
                default: { sourceId: '', cgi: '', code: '', paramType: 'js' },
                ui: { type: 'XySourceCgiEdit' },
                'uc:allowReset': false,
                'uc:allowExpr': false,
            },
        ],
    },
    paramsFormComponents: [XySourceCgiEditMeta],
    hideReturnValueSchema: true,
    async execute(ctx, params) {
        const { sourceId, cgi, code, paramType } = params.cgiInfo || {};
        const state = ctx.renderer.sources[sourceId]?.data;
        const request = state?.$requests[cgi];
        if (typeof request !== 'function') {
            throw new Error($t('k_409f979f', { arg_0: cgi }));
        }
        const fetchFn = async (req) => {
            try {
                const res = await request(req, { raw: true, batchEnabled: true });
                return res;
            }
            catch (error) {
                return {
                    error,
                };
            }
        };
        // 批量模式目前只能js传参
        if (!paramType || paramType === 'js') {
            const httpParams = ctx.tryEval(code); // 空也能调用
            return await fetchFn(httpParams);
        }
    },
    returnValue: {
        type: 'array',
        items: {
            type: 'object',
            fields: [
                {
                    id: 'body',
                    type: 'object',
                    description: $t('k_d51661c7'),
                },
                {
                    id: 'error',
                    type: 'object',
                    description: $t('k_c9e1a874'),
                },
            ],
        },
    },
};
// 数据源操作
export const dsMethodAtom = {
    id: 'xy:invokeDataSourceMethod',
    name: $t('k_237ad5c'),
    category: $t('k_ccd9cc09'),
    returnValue: (params, { designer, renderer }) => {
        const { dataPath, methodName } = params;
        const defaultSchema = {
            type: 'object',
        };
        if (!dataPath || !methodName)
            return defaultSchema;
        const dataSourceId = /data.([^.]+)/.exec(dataPath)?.[1];
        if (!dataSourceId)
            return defaultSchema;
        const dataSchema = getDataSourceMethodReturnSchema(designer, renderer, dataSourceId, methodName) || defaultSchema;
        return dataSchema;
    },
    hideReturnValueSchema: true,
    paramsSchema: {
        type: 'object',
        ui: { type: 'XySourceMethodEdit' },
        fields: [
            {
                id: 'dataPath',
                title: $t('k_23853ce8'),
                type: 'string',
                default: '',
            },
            {
                id: 'methodName',
                type: 'string',
                title: $t('k_f508bc5'),
                default: '',
            },
            {
                id: 'arguments',
                type: 'array',
                items: { type: 'string' },
                title: $t('k_bc85a2de'),
                default: [],
            },
            {
                id: 'argumentsType', // 'none' 'js' 'form'
                type: 'string',
                default: 'none',
            },
            {
                id: 'formArguments',
                items: { type: 'any' },
                title: $t('k_bc85a2de'),
                default: {},
                // JOSN.stringify会把 [undefined,{}]转成[null,{}] 使用对象存arguments
                // [boolean ,{body,query,bodyExprStr}] => {'0': true ,'1':{body,query,bodyExprStr} } 数据源的参数一般是这样 http请求参数需要特殊处理
            },
            {
                id: 'extraInfo',
                type: 'object',
                required: false,
            },
        ],
    },
    paramsFormComponents: [XySourceMethodEditMeta],
    execute(ctx, params) {
        const { dataPath, methodName, argumentsType, arguments: args = [], extraInfo, formArguments } = params;
        const data = get(ctx.renderer, splitPath(dataPath));
        if (!data)
            throw new Error($t('k_c199ae21', { arg_0: dataPath }));
        const fn = data[methodName];
        if (typeof fn !== 'function')
            throw new Error($t('k_9eb9d1d2', { arg_0: dataPath, arg_1: methodName }));
        const isJSModel = getIsJSModel(ctx, dataPath);
        // 新数据源只支持js模式
        if (argumentsType === 'js' || isJSModel) {
            const finalArgs = args.map(expr => (expr ? ctx.tryEval(expr) : undefined));
            return fn.apply(data, finalArgs);
        }
        if (argumentsType === 'none' || !argumentsType) {
            return fn.apply(data);
        }
        if (argumentsType === 'form') {
            const argsArray = Array.isArray(formArguments)
                ? formArguments
                : Object.keys(formArguments).reduce((prev, index) => {
                    set(prev, index, formArguments[index]);
                    return prev;
                }, []);
            const finalArgs = argsArray.map((arg, index) => {
                if (extraInfo?.requestParamsIndex === index) {
                    const { body, query, bodyExprStr } = arg;
                    const isDB = extraInfo?.requestParamsType === 'db';
                    const queryValue = deepInterop(query, ctx.interopContext, ctx.extraInteropContext);
                    let bodyValue = deepInterop(isDB ? bodyExprStr : body, ctx.interopContext, ctx.extraInteropContext);
                    if (isDB && typeof bodyValue === 'string') {
                        try {
                            bodyValue = JSON.parse(bodyValue);
                        }
                        catch (error) {
                            console.error($t('k_1fff1378'), error);
                        }
                    }
                    return {
                        query: queryValue,
                        body: bodyValue,
                    };
                }
                return deepInterop(arg, ctx.interopContext, ctx.extraInteropContext);
            });
            return fn.apply(data, finalArgs);
        }
    },
    getDisplayName(step) {
        const { dataPath, methodName } = step.params;
        if (!dataPath || !methodName)
            return undefined;
        return `${dataPath}.${methodName}`;
    },
};
const getIsJSModel = (ctx, dataPath) => {
    try {
        const is$methods = dataPath.endsWith('.$methods');
        if (!is$methods)
            return false;
        const statePath = dataPath.replace(/\.\$methods$/, '');
        const state = get(ctx.renderer, splitPath(statePath));
        return state?.isJSStateModel;
    }
    catch (error) {
        console.error(error);
        return false;
    }
};
// 数据源重新初始化
export const batchInitAtom = {
    id: 'xy:source:batchInitCgi',
    name: $t('k_551045b7'),
    desc: $t('k_4ec7a197'),
    category: CATEGORY.DATASOURCE,
    paramsSchema: {
        type: 'object',
        fields: [],
    },
    async execute(ctx) {
        const { data } = ctx.renderer ?? {};
        if (data) {
            const ans = await Promise.all(Object.values(data).map((instance) => {
                if (instance.isJSStateModel) {
                    // 新数据源实例
                    return instance?.$methods?.init?.() ?? null;
                }
                // 旧数据源实例
                if (instance?.options?.sourceConfig?.initWhenPageLoad) {
                    return instance?.init?.() ?? null;
                    ;
                }
                return null;
            })
                .filter(Boolean));
            return ans;
        }
    },
};
export default [
    dsMethodAtom,
    cgiAtom,
    batchCgiAtom,
    batchInitAtom,
];
